import { useEffect, useState } from "react";
import api, { fmt } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Target } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = ["Emergency Fund", "Vacation", "New Car", "House", "Debt Payoff", "College", "Other"];

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const load = async () => setGoals((await api.get("/goals")).data);
  useEffect(() => { load(); }, []);

  return (
    <div className="px-6 pt-10 pb-6">
      <div className="flex items-center justify-between">
        <h1 className="font-manrope text-2xl font-extrabold tracking-tight text-slate-900">Savings goals</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-goal-btn" className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-slate-800 transition-colors"><Plus size={17} /></button>
          </DialogTrigger>
          <DialogContent className="max-w-sm rounded-3xl">
            <DialogHeader><DialogTitle className="font-manrope">New goal</DialogTitle></DialogHeader>
            <GoalForm onDone={() => { setOpen(false); load(); }} />
          </DialogContent>
        </Dialog>
      </div>

      {goals.length === 0 && (
        <div className="mt-16 text-center text-slate-500">
          <Target className="mx-auto mb-3" size={36} strokeWidth={1.5} />
          <div className="font-medium">No goals yet</div>
          <div className="text-sm mt-1">Set a goal to give your savings direction.</div>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {goals.map((g) => {
          const pct = g.target_amount > 0 ? Math.min(100, (g.current_amount / g.target_amount) * 100) : 0;
          const remaining = Math.max(0, g.target_amount - g.current_amount);
          const monthly = g.target_date ? Math.ceil(remaining / Math.max(1, monthsBetween(new Date(), new Date(g.target_date)))) : null;
          return (
            <div key={g.id} className="rounded-2xl border border-slate-200 p-5" data-testid={`goal-${g.id}`}>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">{g.goal_type}</div>
                  <div className="mt-1 font-manrope font-bold text-slate-900 text-lg">{g.name}</div>
                </div>
                <div className="font-manrope text-xl font-extrabold tracking-tight tabular-nums text-slate-900">{fmt(g.current_amount)} <span className="text-slate-400 text-sm font-semibold">/ {fmt(g.target_amount)}</span></div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-3 flex justify-between text-xs text-slate-500">
                <span>{pct.toFixed(0)}% complete</span>
                {monthly && <span>~{fmt(monthly)}/mo to hit {g.target_date}</span>}
              </div>
              <UpdateBar g={g} onSaved={load} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function monthsBetween(a, b) {
  return Math.max(1, (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()));
}

function GoalForm({ onDone }) {
  const [d, setD] = useState({ name: "", goal_type: "Emergency Fund", target_amount: 5000, current_amount: 0, target_date: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/goals", { ...d, target_date: d.target_date || null });
      toast.success("Goal added");
      onDone();
    } catch { toast.error("Failed"); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-3 mt-2">
      <input data-testid="goal-name-input" required placeholder="Goal name" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none" />
      <Select value={d.goal_type} onValueChange={(v) => setD({ ...d, goal_type: v })}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
      </Select>
      <input data-testid="goal-target-input" type="number" min="0" required placeholder="Target amount" value={d.target_amount} onChange={(e) => setD({ ...d, target_amount: Number(e.target.value) })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none" />
      <input data-testid="goal-current-input" type="number" min="0" placeholder="Current amount" value={d.current_amount} onChange={(e) => setD({ ...d, current_amount: Number(e.target.value) })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none" />
      <input data-testid="goal-date-input" type="date" value={d.target_date} onChange={(e) => setD({ ...d, target_date: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none" />
      <button disabled={busy} data-testid="goal-submit-btn" className="w-full bg-slate-900 text-white rounded-full py-3 font-semibold hover:bg-slate-800 transition-colors">{busy ? "…" : "Create goal"}</button>
    </form>
  );
}

function UpdateBar({ g, onSaved }) {
  const [v, setV] = useState(g.current_amount);
  const save = async () => {
    await api.patch(`/goals/${g.id}`, { current_amount: Number(v) });
    toast.success("Updated");
    onSaved();
  };
  return (
    <div className="mt-4 flex items-center gap-2">
      <input type="number" min="0" value={v} onChange={(e) => setV(e.target.value)} className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none" />
      <button onClick={save} className="bg-slate-100 text-slate-900 rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-200 transition-colors">Update</button>
    </div>
  );
}
